'use client'

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import EditIcon from '@mui/icons-material/Edit'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import { Box, Button, MenuItem, Select } from '@mui/material'
import { usePathname, useRouter } from 'next/navigation'

import { RecoverDraftDialogs } from '@/src/components/recover/RecoverDraftDialogs'
import { CamplusLogo } from '@/src/components/ui/CamplusLogo'
import { getStudentStepBySaveStep } from '@/src/config/flows/booking-student.flow'
import { getWorkerStepBySaveStep } from '@/src/config/flows/booking-worker.flow'
import { useStudentBookingStore } from '@/src/lib/store/studentBookingStore'
import { useWorkerBookingStore } from '@/src/lib/store/workerBookingStore'
import { loadDraft, recoverDraft } from '@/src/services/drafts.api'
import type { AdmissionLoadResponse } from '@/src/types/api.types'

const SUPPORTED_LOCALES = ['it', 'en', 'fr', 'es']

export const BookingHeader: React.FC = () => {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()
  const safePathname = pathname ?? ''
  const locale = safePathname.split('/')[1] || 'it'
  const resetStudentStore = useStudentBookingStore((state) => state.resetStore)
  const resetWorkerStore = useWorkerBookingStore((state) => state.resetStore)

  // Recover draft state
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [openRecover, setOpenRecover] = useState(false)
  const [openRecoverEmail, setOpenRecoverEmail] = useState(false)
  const [recoverEmail, setRecoverEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [loadingRecover, setLoadingRecover] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recoverSuccess, setRecoverSuccess] = useState(false)
  const [draftLoaded, setDraftLoaded] = useState(false)

  const handleFetchDraft = async () => {
    if (!email.trim() || !code.trim()) {
      setError(t('recover.missingCredentials'))
      return
    }

    setLoading(true)
    setError(null)
    setDraftLoaded(false)

    try {
      const data: AdmissionLoadResponse = await loadDraft(email.trim(), code.trim())

      if (data.error) {
        setError(data.errorMessages?.[0] || data.message || t('recover.error'))
      } else if (data.body && !data.body.closed) {
        const occupazione = data.body.steps?.[0]?.values?.find((v) => v.name === 'occupazione')
        const isWorker = occupazione?.value === '1'
        const basePath = isWorker ? `/${locale}/booking-worker` : `/${locale}/booking-student`
        const currentSaveStep = data.body.steps?.length || 1
        const stepSlug = isWorker
          ? (getWorkerStepBySaveStep(currentSaveStep)?.slug ?? 'registration')
          : (getStudentStepBySaveStep(currentSaveStep)?.slug ?? 'registration')

        setDraftLoaded(true)
        setOpenRecover(false)
        setEmail('')
        setCode('')
        window.location.href = `${basePath}/${stepSlug}/?draftId=${data.body.id}&email=${encodeURIComponent(email.trim())}&code=${encodeURIComponent(code.trim())}`
      } else {
        setError(t('recover.noDraftsFound'))
      }
    } catch (err) {
      console.error('Error recovering draft:', err)
      setError(t('recover.error'))
    } finally {
      setLoading(false)
    }
  }

  const handleRequestCode = async () => {
    if (!recoverEmail.trim()) {
      setError(t('recover.emailRequired'))
      return
    }

    setLoadingRecover(true)
    setError(null)
    setRecoverSuccess(false)

    try {
      const data = await recoverDraft(recoverEmail.trim(), locale)

      if (data?.returnCode === 'OK') {
        setRecoverSuccess(true)
        setRecoverEmail('')
      } else {
        setError(t('recover.error'))
      }
    } catch (err) {
      console.error('Error requesting code:', err)
      setError(t('recover.error'))
    } finally {
      setLoadingRecover(false)
    }
  }

  const handleRestartApplication = () => {
    resetStudentStore()
    resetWorkerStore()

    window.localStorage.removeItem('camplus-student-booking')
    window.localStorage.removeItem('camplus-worker-booking')

    window.location.href = `/${locale}/`
  }

  return (
    <Box component="header" sx={{ position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          bgcolor: 'primary.main',
          height: { xs: 70, md: 100 }
        }}
      >
        {/* Left: "RICOMINCIA DOMANDA" button */}
        <Box
          onClick={handleRestartApplication}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'secondary.main',
            height: '100%',
            px: { xs: 2, md: 5 },
            cursor: 'pointer',
            '&:hover': { bgcolor: 'secondary.dark' },
            transition: 'background-color 0.2s'
          }}
        >
          <RestartAltIcon sx={{ width: 20, color: 'white' }} />
          <Box
            component="span"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontFamily: '"Barlow Condensed", sans-serif',
              fontWeight: 700,
              fontSize: '1.4rem',
              lineHeight: 1.7,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'white'
            }}
          >
            {t('header.restart')}
          </Box>
        </Box>

        {/* Center: blue area with logo */}
        <Box
          sx={{
            flex: 1,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            left: '50%',
            top: 0,
            transform: 'translateX(-50%)',
            pointerEvents: 'none'
          }}
        >
          <CamplusLogo width={120} />
        </Box>

        {/* Right: Resume button + Language selector */}
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: { xs: 2, md: 5 }
          }}
        >
          <Button
            size="small"
            onClick={() => setOpenRecover(true)}
            sx={{
              fontFamily: '"Barlow Condensed", sans-serif',
              fontWeight: 700,
              fontSize: '1.4rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)'
              },
              minWidth: 'auto',
              padding: { xs: '6px 8px', md: '12px 24px' }
            }}
          >
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>{t('header.completeRequest')}</Box>
            <EditIcon sx={{ display: { xs: 'flex', md: 'none' }, fontSize: '1.8rem' }} />
          </Button>

          <Select
            value={i18n.resolvedLanguage ?? 'it'}
            onChange={(e) => {
              const newLocale = e.target.value
              const segments = safePathname.split('/')
              // segments[0] is '', segments[1] is the locale
              if (SUPPORTED_LOCALES.includes(segments[1])) {
                segments[1] = newLocale
              }
              router.push(segments.join('/') || '/')
            }}
            sx={{
              fontFamily: '"Barlow Condensed", sans-serif',
              fontWeight: 700,
              fontSize: '1.4rem',
              color: 'white',
              textTransform: 'uppercase',
              minWidth: 70,
              '& .MuiSelect-select': {
                color: 'white !important',
                fontSize: '1.4rem !important',
                fontWeight: '700 !important',
                display: 'flex !important',
                alignItems: 'center !important',
                paddingTop: '0 !important',
                paddingBottom: '0 !important'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent'
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
                top: '50%',
                transform: 'translateY(-50%)'
              }
            }}
          >
            <MenuItem
              value="it"
              sx={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700 }}
            >
              IT
            </MenuItem>
            <MenuItem
              value="en"
              sx={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700 }}
            >
              EN
            </MenuItem>
            <MenuItem
              value="fr"
              sx={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700 }}
            >
              FR
            </MenuItem>
            <MenuItem
              value="es"
              sx={{ fontFamily: '"Barlow Condensed", sans-serif', fontWeight: 700 }}
            >
              ES
            </MenuItem>
          </Select>
        </Box>
      </Box>

      <RecoverDraftDialogs
        open={openRecover}
        onOpenChange={setOpenRecover}
        openRecoverEmail={openRecoverEmail}
        onOpenRecoverEmailChange={setOpenRecoverEmail}
        email={email}
        setEmail={setEmail}
        code={code}
        setCode={setCode}
        recoverEmail={recoverEmail}
        setRecoverEmail={setRecoverEmail}
        loading={loading}
        loadingRecover={loadingRecover}
        error={error}
        draftLoaded={draftLoaded}
        recoverSuccess={recoverSuccess}
        handleFetchDraft={handleFetchDraft}
        handleRequestCode={handleRequestCode}
      />
    </Box>
  )
}
